import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { automationManager } from '@/modules/automation/services'
import type { AutomationPriority } from '@/modules/automation/types'
import { toastError, toastSuccess } from '@/stores'
import { useAutomationStore } from '@/stores/automation-store'

const QUERY_KEY = ['automation'] as const

export function useAutomation() {
  const queryClient = useQueryClient()
  const hydrate = useAutomationStore((s) => s.hydrate)
  const refresh = useAutomationStore((s) => s.refresh)
  const ready = useAutomationStore((s) => s.ready)
  const dashboard = useAutomationStore((s) => s.dashboard)
  const jobs = useAutomationStore((s) => s.jobs)
  const sessions = useAutomationStore((s) => s.sessions)
  const logs = useAutomationStore((s) => s.logs)
  const events = useAutomationStore((s) => s.events)
  const notifications = useAutomationStore((s) => s.notifications)
  const config = useAutomationStore((s) => s.config)
  const filters = useAutomationStore((s) => s.filters)
  const setFilters = useAutomationStore((s) => s.setFilters)
  const updateConfig = useAutomationStore((s) => s.updateConfig)
  const enqueueStore = useAutomationStore((s) => s.enqueue)

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      await automationManager.pump()
      return automationManager.dashboard()
    },
    refetchInterval: 2000,
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const invalidate = useCallback(() => {
    refresh()
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  }, [refresh, queryClient])

  const enqueue = useCallback(
    (input: {
      supplier: string
      operation: string
      priority?: AutomationPriority
      payload?: Record<string, unknown>
    }) => {
      const task = enqueueStore(input)
      toastSuccess('Automação', `${task.supplier} · ${task.operation}`)
      setTimeout(() => invalidate(), 100)
      return task
    },
    [enqueueStore, invalidate],
  )

  const cancel = useCallback(
    (taskId: string) => {
      const result = automationManager.cancel(taskId)
      if (!result) toastError('Cancelamento', 'Tarefa não encontrada na fila')
      else toastSuccess('Cancelada', result.id)
      invalidate()
      return result
    },
    [invalidate],
  )

  return {
    ready,
    dashboard: query.data ?? dashboard,
    jobs,
    sessions,
    logs,
    events,
    notifications,
    config,
    filters,
    setFilters,
    updateConfig,
    enqueue,
    cancel,
    refresh: invalidate,
    isLoading: query.isLoading,
    manager: automationManager,
  }
}
